import {
  IS_MOBILE_FULL_USER_AGENT_REGEX,
  IS_MOBILE_PARTIAL_USER_AGENT_REGEX,
  NUMBER_0,
  NUMBER_1,
  NUMBER_10000,
  NUMBER_1200,
  NUMBER_1500,
  NUMBER_180,
  NUMBER_4,
  NUMBER_768,
  NUMBER_992,
  OPTIMIZELY_DATA_LOCALSTORAGE_KEY,
} from '~shared/constants';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { UserAttributes } from '@optimizely/optimizely-sdk';

type TrafficSource =
  | 'Google Paid'
  | 'Google Organic'
  | 'Other CPC'
  | 'Other Retargeting'
  | 'Email'
  | 'Direct'
  | 'Other'
  | undefined;

type BreakpointClass = 'S-to-M' | 'M-to-L' | 'L-to-XL' | 'XL-to-XXL' | 'XXL-and-up' | 'undefined' | undefined;

type OptimizelyDataStorage = {
  last_session_timestamp?: number;
  optly_new_visitor?: boolean;
  traffic_source?: TrafficSource;
  new_visitor?: boolean;
};

interface OptimizelyDefaultAttributes extends UserAttributes {
  is_mobile: boolean;
  breakpoint_class: BreakpointClass;
  country: string;
  traffic_source: TrafficSource;
  is_new_visit: boolean;
}

export namespace OptimizelyAttributesUtil {
  function getTrafficSource(): TrafficSource {
    let trafficSource;
    const url = window.location.href.toLowerCase();
    const host = location.host.replace('www.', '').toLowerCase();

    if (url.match('gclid|wbraid')) {
      trafficSource = 'Google Paid';
    } else if (host.match('^google.') && !url.match('utm_')) {
      trafficSource = 'Google Organic';
    } else if (url.match('utm_medium=cpc') && host !== '') {
      trafficSource = 'Other CPC';
    } else if (url.match('utm_medium=retargeting') && host !== '') {
      trafficSource = 'Other Retargeting';
    } else if (url.match('utm_medium=email') && host !== '') {
      trafficSource = 'Email';
    } else if (host === '') {
      trafficSource = 'Direct';
    } else if (host !== '') {
      trafficSource = 'Other';
    }

    return trafficSource;
  }

  function getBreakpointClass(): BreakpointClass {
    let text;

    const width = document.documentElement.clientWidth;
    const widthNumber = parseInt(width as any);

    if (isNaN(widthNumber)) {
      text = 'undefined';
      return text;
    }

    if (widthNumber >= NUMBER_1 && widthNumber <= NUMBER_768 - NUMBER_1) {
      text = 'S-to-M';
    } else if (widthNumber >= NUMBER_768 && widthNumber <= NUMBER_992 - NUMBER_1) {
      text = 'M-to-L';
    } else if (widthNumber >= NUMBER_992 && widthNumber <= NUMBER_1200 - NUMBER_1) {
      text = 'L-to-XL';
    } else if (widthNumber >= NUMBER_1200 && widthNumber <= NUMBER_1500 - NUMBER_1) {
      text = 'XL-to-XXL';
    } else if (widthNumber >= NUMBER_1500) {
      text = 'XXL-and-up';
    } else {
      text = 'undefined';
    }

    return text;
  }

  function getIsMobile(): boolean {
    if (
      IS_MOBILE_FULL_USER_AGENT_REGEX.test(navigator.userAgent) ||
      IS_MOBILE_PARTIAL_USER_AGENT_REGEX.test(navigator.userAgent.slice(NUMBER_0, NUMBER_4))
    ) {
      return true;
    }
    return false;
  }

  export function getDefaultAttributes(): OptimizelyDefaultAttributes {
    //calculating session attributes
    let optimizelyData: OptimizelyDataStorage = {};
    const optimizelyCurrentTimestamp = Date.now();

    // returning sessions
    if (!!localStorage.getItem(OPTIMIZELY_DATA_LOCALSTORAGE_KEY)) {
      optimizelyData = JSON.parse(localStorage.getItem(OPTIMIZELY_DATA_LOCALSTORAGE_KEY));
      // identifying new sessions and update traffic source
      if (NUMBER_180 * NUMBER_10000 + optimizelyData.last_session_timestamp < optimizelyCurrentTimestamp) {
        optimizelyData.traffic_source = getTrafficSource();
      }
      optimizelyData.optly_new_visitor = false;
    } else {
      //new visitors
      optimizelyData.optly_new_visitor = true;
      optimizelyData.traffic_source = getTrafficSource();
    }

    optimizelyData.last_session_timestamp = optimizelyCurrentTimestamp;
    localStorage.setItem(OPTIMIZELY_DATA_LOCALSTORAGE_KEY, JSON.stringify(optimizelyData));

    const hasUserSeenFoodStorageValue: string = sessionStorage?.getItem('hasUserSeenFood');

    return {
      is_mobile: getIsMobile(),
      breakpoint_class: getBreakpointClass(),
      country: getCountryDomainCode(),
      traffic_source: optimizelyData.traffic_source,
      is_new_visit: optimizelyData.optly_new_visitor,
      has_user_seen_food: (hasUserSeenFoodStorageValue && hasUserSeenFoodStorageValue === 'true') || false,
    };
  }
}
